import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Check, AlertCircle, FileText, Eraser, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

function SignaturePad({ canvasRef, onChange }) {
  const drawing = useRef(false);
  const pos = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#1f2328"; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); onChange(); };
  const end = () => { drawing.current = false; };
  return (
    <canvas
      ref={canvasRef} width={460} height={130} data-testid="signature-canvas"
      className="border border-stone-300 rounded-lg w-full bg-white touch-none cursor-crosshair"
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
    />
  );
}

export default function SignDevis() {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [cgv, setCgv] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/billing/public/devis/${token}`)
      .then((r) => { setData(r.data); setName(r.data.client_nom || ""); })
      .catch((e) => setError(e.response?.data?.detail || "Lien invalide ou expiré"));
  }, [token]);

  const clearSig = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasSig(false); };

  const sign = async () => {
    if (!name.trim()) { setError("Veuillez indiquer votre nom complet."); return; }
    if (!cgv) { setError("Vous devez accepter les Conditions Générales de Vente."); return; }
    if (!hasSig) { setError("Veuillez tracer votre signature dans le cadre."); return; }
    setError(null); setSending(true);
    try {
      const image = canvasRef.current.toDataURL("image/png");
      const r = await axios.post(`${API}/billing/public/devis/${token}/sign`, { signed_name: name.trim(), cgv_accepted: true, signature_image: image });
      setDone(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Erreur lors de la signature."); } finally { setSending(false); }
  };

  if (error && !data) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center border border-stone-200">
        <AlertCircle size={32} className="mx-auto text-rose-500 mb-3"/>
        <div className="font-bold text-xl mb-2">Lien indisponible</div>
        <p className="text-sm text-stone-500">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-stone-500 text-sm">Chargement…</div>;

  const accent = data.societe_code === "FM" ? "#4FB3A6" : "#C2502F";
  const alreadySigned = data.signature?.signed;

  if (done || alreadySigned) return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center border border-stone-200" data-testid="sign-success">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: accent }}><Check size={28} className="text-white"/></div>
        <div className="font-bold text-xl mb-2">Devis signé ✔</div>
        <p className="text-sm text-stone-500">Le devis <b>{data.numero}</b> a bien été signé. Une copie vous sera transmise. Merci de votre confiance !</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="sign-page">
      <div className="max-w-2xl mx-auto">
        <div className="h-1.5 rounded-t-xl" style={{ background: accent }}/>
        <div className="bg-white rounded-b-xl border border-stone-200 border-t-0 p-6 sm:p-8">
          <div className="text-xs uppercase tracking-widest text-stone-400 mb-1">{data.societe_nom}</div>
          <h1 className="font-bold text-2xl mb-1">Signature du devis {data.numero}</h1>
          <p className="text-sm text-stone-500 mb-5">{data.objet || data.projet_nom}</p>

          <div className="border border-stone-200 rounded-lg overflow-hidden mb-4">
            {data.lignes.map((l, i) => (
              <div key={i} className="flex justify-between gap-3 px-4 py-2.5 text-sm border-b border-stone-100 last:border-0">
                <span className="text-stone-700">{l.designation}{l.qte ? ` × ${l.qte}` : ""}</span>
                <span className="font-mono text-stone-900 whitespace-nowrap">{eur((l.qte || 1) * (l.pu_ht || 0))} HT</span>
              </div>
            ))}
            <div className="flex justify-between px-4 py-3 bg-stone-50 font-bold">
              <span>Total TTC</span><span className="font-mono" style={{ color: accent }}>{eur(data.total_ttc)}</span>
            </div>
          </div>

          <a href={`${API}/billing/public/devis/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="sign-view-pdf" className="inline-flex items-center gap-2 text-sm font-semibold mb-6" style={{ color: accent }}>
            <FileText size={16}/> Consulter le devis complet (PDF avec CGV)
          </a>

          <label className="flex flex-col text-xs font-semibold text-stone-600 mb-4">Votre nom complet
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="sign-name" placeholder="Prénom NOM" className="mt-1 border border-stone-300 rounded px-3 py-2 font-normal text-sm"/>
          </label>

          <div className="text-xs font-semibold text-stone-600 mb-1 flex items-center justify-between">
            <span>Votre signature</span>
            <button onClick={clearSig} type="button" className="text-stone-400 hover:text-stone-700 inline-flex items-center gap-1 font-normal"><Eraser size={12}/> Effacer</button>
          </div>
          <SignaturePad canvasRef={canvasRef} onChange={() => setHasSig(true)}/>

          <label className="flex items-start gap-2 text-sm text-stone-700 my-5 cursor-pointer">
            <input type="checkbox" checked={cgv} onChange={(e) => setCgv(e.target.checked)} data-testid="sign-cgv" className="mt-0.5"/>
            <span>J'ai lu et j'accepte les <a href={`${API}/billing/public/devis/${token}/pdf`} target="_blank" rel="noreferrer" className="underline" style={{ color: accent }}>Conditions Générales de Vente</a>, et la mention « Bon pour accord » vaut acceptation pleine et entière du devis.</span>
          </label>

          {error && <div className="text-sm text-rose-600 mb-3" data-testid="sign-error">{error}</div>}

          <button onClick={sign} disabled={sending} data-testid="sign-submit" className="w-full py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: accent }}>
            {sending ? <Loader2 size={16} className="animate-spin"/> : <Check size={16}/>} Signer le devis
          </button>
          <p className="text-[11px] text-stone-400 mt-3 text-center">Signature électronique horodatée et tracée. Cette action vaut engagement contractuel.</p>
        </div>
      </div>
    </div>
  );
}
